import { JOINT_LABELS, PLANE_LABELS, type AxisDemand } from './drives'
import { DRIVE_OVERHEAD_BUDGET, SET_AXES, setAxisFor, type SetAxis } from './motorisation'
import type { HumanoidModel } from './model'
import { carrying, type SimState } from './state'

/**
 * Die Stückliste — die Brücke zwischen Rechnung und Konstruktion.
 *
 * DIE SEITE SCHLIESST DEN KREIS. Die Antriebsliste sagt, welches Moment jede
 * Achse braucht. Ein Moment braucht einen Aktuator, ein Aktuator hat eine
 * Masse, und diese Masse muss wieder getragen werden. Genau diese Schleife
 * rechnet die Stückliste aus — bis zum Fixpunkt.
 *
 * DIE RÜCKKOPPLUNG IST DER GRUND, DASS ES DIESE SEITE GIBT:
 *
 *     m′ = (a·m + c) / (1 − k)        Fixpunkt  m* = c / ((1 − k) − a)
 *
 * `a` ist der Teil der Masse, der mit der Masse wächst (Antriebe, Akku,
 * Kondensator), `c` der feste Teil (Elektronik, Sensorik, Hände und Füsse), `k`
 * der Strukturanteil. Nähert sich `(1 − k) − a` der Null, divergiert der
 * Entwurf: die Maschine kann ihre eigenen Antriebe nicht mehr tragen. Das ist
 * dieselbe Aussage wie der Rückkopplungsfaktor auf der Balance-Seite, hier aber
 * mit den wirklich gerechneten Momenten statt mit einem angenommenen.
 *
 * JEDE ZEILE IST ÜBERSCHREIBBAR. Der Schätzwert bleibt daneben stehen. Sobald
 * eine Baugruppe gewogen ist, trägt man sie ein und die Summe rechnet mit dem
 * echten Wert weiter — genau der Abgleich, den die Massenseite verlangt.
 */

/* -------------------------------------------------------------------------
   Die Eingaben
   ------------------------------------------------------------------------- */

export interface BomParams {
  /** Momentendichte des Antriebsmoduls, Nm/kg. */
  torqueDensity: number
  /** Sicherheitsfaktor auf das gerechnete Moment. */
  safety: number
  /** Energiedichte des Akkupacks, Wh/kg. */
  batteryDensity: number
  /** Geforderte Betriebszeit, h. */
  runtime: number
  /** Strukturanteil an der Gesamtmasse. */
  structureShare: number
  /** Systemspannung, V. */
  busVoltage: number
  /** Zulässiger Spannungseinbruch am Zwischenkreis, %. */
  voltageDrop: number
  /** Lastschwerpunkt vor dem Rumpf, m. */
  loadLever: number
  /** Abtriebsdrehzahl des Antriebsmoduls, °/s. */
  moduleSpeed: number
}

/*
  HIER STAND EINMAL EIN REGLER `recovery` — ein pauschaler Rückspeisegrad, mit
  dem die negative Nettoarbeit der verlustfreien inversen Dynamik in eine Zahl
  verwandelt wurde, die man einkaufen kann. Er ist ersatzlos entfallen.

  Den Dienst tut jetzt `computePower` in `power.ts`: Feder je Gelenk,
  gemeinsamer Zwischenkreis, Halte- und Elektronikverluste, jedes einzeln
  zuschaltbar. Das ist dieselbe Frage, nur beantwortet statt geschätzt — und
  zwei Regler für dieselbe Grösse nebeneinander wären genau die Sorte
  Doppelbuchung, an der später niemand mehr erkennt, welche Zahl gilt.
*/

export const DEFAULT_BOM: BomParams = {
  torqueDensity: 45,
  safety: 1.25,
  batteryDensity: 200,
  runtime: 3,
  structureShare: 0.32,
  busVoltage: 48,
  voltageDrop: 25,
  loadLever: 0.3,
  moduleSpeed: 400,
}

export interface BomField {
  key: keyof BomParams
  label: string
  min: number
  max: number
  step: number
  unit: string
  digits: number
}

export const BOM_FIELDS: BomField[] = [
  {
    key: 'torqueDensity',
    label: 'Momentendichte des Antriebsmoduls',
    min: 10,
    max: 130,
    step: 1,
    unit: 'Nm/kg',
    digits: 0,
  },
  {
    key: 'safety',
    label: 'Sicherheitsfaktor auf das Moment',
    min: 1,
    max: 2.5,
    step: 0.05,
    unit: '',
    digits: 2,
  },
  {
    key: 'batteryDensity',
    label: 'Energiedichte des Akkupacks',
    min: 80,
    max: 350,
    step: 5,
    unit: 'Wh/kg',
    digits: 0,
  },
  {
    key: 'runtime',
    label: 'Geforderte Betriebszeit',
    min: 0.5,
    max: 8,
    step: 0.5,
    unit: 'h',
    digits: 1,
  },
  {
    key: 'structureShare',
    label: 'Strukturanteil an der Gesamtmasse',
    min: 0.15,
    max: 0.5,
    step: 0.01,
    unit: '',
    digits: 2,
  },
  { key: 'busVoltage', label: 'Systemspannung', min: 24, max: 120, step: 1, unit: 'V', digits: 0 },
  {
    key: 'voltageDrop',
    label: 'Zulässiger Spannungseinbruch',
    min: 5,
    max: 50,
    step: 1,
    unit: '%',
    digits: 0,
  },
  {
    key: 'loadLever',
    label: 'Lastschwerpunkt vor dem Rumpf',
    min: 0.05,
    max: 0.5,
    step: 0.01,
    unit: 'm',
    digits: 2,
  },
  {
    key: 'moduleSpeed',
    label: 'Abtriebsdrehzahl des Moduls',
    min: 60,
    max: 900,
    step: 10,
    unit: '°/s',
    digits: 0,
  },
]

/* -------------------------------------------------------------------------
   Die Achsen
   ------------------------------------------------------------------------- */

/**
 * WOHER DIE MASSE EINER POSITION KOMMT.
 *
 * Nicht zu verwechseln mit `BomSource` weiter unten — das sagt, woher das
 * MOMENT kommt. Diese Stufen sagen, woher das KILOGRAMM kommt, und sie sind
 * nach abnehmender Verbindlichkeit geordnet:
 *
 *   plm            Gemessen am Bauteil, aus dem fuehrenden System. Die einzige
 *                  Stufe, die nicht in diesem Repository entstanden ist.
 *   ueberschrieben Von Hand eingetragen. Schlaegt alles darunter, weil ein
 *                  Mensch etwas weiss, was die Rechnung nicht weiss.
 *   gesetzt        Aus einer getroffenen Auswahl — heute die Motorisierungs-
 *                  matrix der Sammelmappe.
 *   geschaetzt     Hochgerechnet aus Moment und Momentendichte, oder ein
 *                  Erfahrungswert. Eine Vermutung mit Einheit.
 *
 * WARUM DAS UEBERHAUPT AM DATENSATZ STEHT. Eine Gesamtmasse, in der eine
 * Messung und eine Faustformel ununterscheidbar addiert werden, traegt keine
 * Entscheidung. Man kann sie nennen, aber nicht verantworten.
 */
export type MassSource = 'plm' | 'ueberschrieben' | 'gesetzt' | 'geschaetzt'

/**
 * Der Nachweis aus dem PLM zu EINER Position der Stueckliste.
 *
 * ABSICHTLICH KEIN `PlmPart`. Die Stueckliste liegt im Rechenkern, das PLM ist
 * eine Aussenanbindung — der Kern soll den Vertrag der Gegenstelle nicht
 * kennen. Was hier steht, ist das Wenige, das eine Zeile braucht, um sagen zu
 * koennen: diese Zahl ist gemessen, hier ist die Teilenummer dazu.
 *
 * HEUTE FUELLT DAS NIEMAND. Das PLM ist angebunden, liefert aber noch keine
 * Zuordnung von Teilen zu Stuecklistenpositionen (`PlmPart.bomKey`). Bis dahin
 * steht ueberall `null`, und die Oberflaeche sagt das auch — eine leere Spalte
 * ist eine ehrlichere Auskunft als eine, die es nicht gibt.
 */
export interface BomEvidence {
  /** Teilenummer im PLM. */
  number: string
  revision: string
  /** Reifegrad im Klartext, wie ihn das PLM fuehrt. */
  state: string
  /** Gemessene Masse je Stueck, kg. */
  mass: number
}

/**
 * Die Nachweise, nach Stuecklistenschluessel.
 *
 * Der Kern bekommt sie fertig hereingereicht und holt sie nicht selbst. Wer
 * nichts hat, reicht nichts herein — der Standardfall, heute und bis die
 * Gegenstelle liefert.
 */
export type BomEvidenceMap = Readonly<Record<string, BomEvidence>>

export type BomSource = 'inverse Dynamik' | 'statisch geschätzt' | 'passiv' | 'gesetzt'

export interface BomAxis {
  key: string
  group: string
  label: string
  /** Anzahl gleicher Achsen. */
  count: number
  /** Gerechnetes oder geschätztes Spitzenmoment, Nm. */
  moment: number
  /** Mit Sicherheitsfaktor, Nm. */
  required: number
  /** Spitzendrehzahl am Abgang, °/s. Null, wenn nicht gerechnet. */
  speed: number
  /** Verlangt die Achse mehr Drehzahl, als das Modul liefert? */
  tooFast: boolean
  /** Geschätzte Einzelmasse, kg. */
  estimate: number
  /** Verwendete Einzelmasse — gesetzt, überschrieben oder geschätzt. */
  mass: number
  massSource: MassSource
  /** Der Nachweis aus dem PLM — `null`, solange keiner vorliegt. */
  evidence: BomEvidence | null
  /** Die gesetzte Achse, falls es eine gibt — für die Anzeige von Motor und Übersetzung. */
  setup: SetAxis | null
  overridden: boolean
  source: BomSource
  note?: string
}

const G = 9.81
const DEG = Math.PI / 180

/**
 * Statische Momentabschätzung für Achsen, die der Kern nicht rechnet.
 *
 * Segmentmasse mal Hebel, dazu die anteilige Nutzlast an ihrem eigenen Hebel,
 * beides mal g mal einem dynamischen Zuschlag. Der Lastanteil ist nicht
 * Kosmetik: ohne ihn wären Rumpf und Arme im Tragefall grob unterschätzt — dort
 * hängt die Last am längsten Hebel des ganzen Aufbaus.
 */
function staticMoment(
  segmentMass: number,
  lever: number,
  dynamic: number,
  load: number,
  loadShare = 0,
  loadLever = 0,
): number {
  return (segmentMass * lever + loadShare * load * loadLever) * G * dynamic
}

/**
 * Welche Achsen der Kern selbst rechnet und welche geschätzt werden.
 *
 * UNTERSCHIED ZUR VORLAGE: dort sind Schulter und Ellbogen statisch geschätzt.
 * Hier kommen sie aus der inversen Dynamik der Armkette und stehen deshalb mit
 * ihrem gerechneten Moment in der Liste. Geschätzt bleiben nur Rumpf und Hals —
 * für die gibt es keine Winkelbahn, aus der sich ein Moment ableiten liesse.
 */
/** Die gesetzte Achse über ihren Schlüssel — für die Rumpfzeilen, die nicht
    aus der gerechneten Achsliste kommen. */
const setAxisByKey = (key: string): SetAxis | null =>
  SET_AXES.find((a) => a.key === key) ?? null

export function bomAxes(
  model: HumanoidModel,
  demands: AxisDemand[],
  state: SimState,
  params: BomParams,
  overrides: Record<string, number> = {},
  /* Leer, solange das PLM keine Zuordnung zu Stuecklistenpositionen liefert —
     also heute. Der Weg steht trotzdem offen, damit die Spalte da ist, bevor
     sie gefuellt wird. */
  evidenceMap: BomEvidenceMap = {},
): BomAxis[] {
  const out: BomAxis[] = []
  /*
    Die Zuladung wirkt nur, wenn sie auch getragen wird. `state.load` steht
    immer im Zustand — sie ist eine Eingabe des Tragefalls und keine Eigenschaft
    der Maschine. Wer das übersieht, legt den Rumpfantrieb für einen Karton aus,
    den der Roboter beim Gehen gar nicht in den Händen hat.
  */
  const load = carrying(state) ? state.load : 0

  const push = (
    group: string,
    label: string,
    count: number,
    moment: number,
    source: BomSource,
    speed = 0,
    note?: string,
    /* Woran die Zeile im Modell haengt. Nur damit laesst sich die gesetzte
       Motorisierung finden; die geschaetzten Zeilen (Hals, Hand, Wirbelsaeule)
       reichen hier nichts herein und bleiben Schaetzung. */
    setup: SetAxis | null = null,
  ) => {
    const key = `a:${label}`
    const required = moment * params.safety
    /* Ein Antriebsmodul unter 250 g gibt es nicht — Gehäuse, Lager und Geber
       kommen dazu, auch wenn das Moment klein ist. */
    const estimate = moment > 0 ? Math.max(0.25, required / params.torqueDensity) : 0
    const overridden = overrides[key] !== undefined

    /*
      DIE REIHENFOLGE IST DIE AUSSAGE: Handeingabe schlaegt Auswahl, Auswahl
      schlaegt Hochrechnung.

      Eine getroffene Motorauswahl ist mehr wert als Moment durch
      Momentendichte — die Hochrechnung weiss nichts von Bauraum, Drehzahl oder
      Traegheit und lag an den Beinachsen entsprechend daneben. Sie bleibt
      trotzdem stehen: `estimate` ist die Vergleichszahl, an der man sieht, ob
      die Auswahl leichter oder schwerer ausgefallen ist als die Faustformel.

      Zur Motormasse kommt das mittlere Budget fuer Getriebe, Gehaeuse, Lager
      und Geber. Ein Motor allein ist kein Antrieb.
    */
    const gesetzt = setup
      ? setup.sharedWith
        ? /* Parallelkinematik: die Motoren stehen bei der Partnerzeile. Eine
             zweite Masse hier zählte dieselben zwei Motoren doppelt. */
          0
        : setup.mass + DRIVE_OVERHEAD_BUDGET
      : null
    const evidence = evidenceMap[key] ?? null

    /*
      DIE RANGFOLGE, UND SIE IST DIE GANZE AUSSAGE DIESER FUNKTION.

      Handeingabe schlaegt PLM: wer eine Zahl von Hand setzt, weiss etwas, was
      weder Messung noch Rechnung weiss — meist, dass sich das Teil gerade
      aendert. Sonst gewinnt die Messung; darunter die getroffene Auswahl,
      zuletzt die Hochrechnung.

      Die jeweils schwaecheren Stufen bleiben am Datensatz stehen. Man soll
      sehen koennen, wovon man abweicht.
    */
    const mass = overridden
      ? overrides[key]!
      : (evidence?.mass ?? gesetzt ?? estimate)
    const massSource: MassSource = overridden
      ? 'ueberschrieben'
      : evidence
        ? 'plm'
        : gesetzt !== null
          ? 'gesetzt'
          : 'geschaetzt'

    out.push({
      key,
      group,
      label,
      count,
      moment,
      required,
      speed,
      tooFast: speed > params.moduleSpeed,
      estimate,
      mass,
      overridden,
      massSource,
      evidence,
      setup,
      source: overridden ? 'gesetzt' : source,
      note,
    })
  }

  /*
    Die gerechneten Achsen. Beide Seiten werden zu EINER Zeile mit Anzahl zwei
    zusammengefasst — der Gang ist symmetrisch, und zwei Zeilen mit derselben
    Zahl wären eine Zeile zu viel. Genommen wird das grössere der beiden
    Momente; wer für die linke Seite einen anderen Antrieb einbaut als für die
    rechte, hat ein anderes Problem als eine Stückliste.
  */
  const grouped = new Map<
    string,
    { label: string; group: string; moment: number; speed: number; setup: SetAxis | null }
  >()
  const GROUP: Record<string, string> = {
    hip: 'Beine',
    knee: 'Beine',
    ankle: 'Beine',
    mtp: 'Beine',
    shoulder: 'Arme',
    elbow: 'Arme',
  }

  for (const demand of demands) {
    if (demand.structural) continue
    const id = `${demand.joint}|${demand.plane}`
    const speed = demand.speed.peak / DEG
    const previous = grouped.get(id)
    if (previous) {
      previous.moment = Math.max(previous.moment, demand.moment.peak)
      previous.speed = Math.max(previous.speed, speed)
    } else {
      grouped.set(id, {
        /* Ohne Seitenkürzel: die Zeile steht für beide Achsen. */
        label: `${JOINT_LABELS[demand.joint] ?? demand.joint} · ${PLANE_LABELS[demand.plane]}`,
        group: GROUP[demand.joint] ?? 'Sonstige',
        moment: demand.moment.peak,
        speed,
        setup: setAxisFor(demand.joint, demand.plane),
      })
    }
  }

  for (const [, entry] of grouped) {
    push(
      entry.group,
      entry.label,
      2,
      entry.moment,
      'inverse Dynamik',
      entry.speed,
      undefined,
      entry.setup,
    )
  }

  /*
    Die geschätzten Achsen. Die Massenanteile sind die des Segmentmodells, damit
    die Schätzung mit demselben Körper rechnet wie alles andere: wer die
    Rumpfmasse auf der Modellseite ändert, ändert auch dieses Moment.
  */
  /*
    Was über dem Rumpfgelenk hängt: Rumpf, beide Arme, Kopf. In KILOGRAMM aus
    dem Modell und nicht als Anteil der Körpermasse — die Vorlage rechnet mit
    Anteilen, dieses Modell führt absolute Massen, und wer einen Arm schwerer
    macht, soll das hier sehen.
  */
  const m = model.masses
  const trunkMass = m.trunk.kg + m.head.kg + 2 * (m.upperArm.kg + m.forearm.kg)
  const headMass = m.head.kg
  const lever = params.loadLever
  const carried = load > 0 ? ' mit Nutzlast' : ''

  /*
    ALLE DREI RUMPFACHSEN SIND MOTORISIERT. Der Gen5-Stand stellt Beugen und
    Seitneigung mit denselben zwei Bauchmotoren über das Kreuzgelenk (die
    Seitneigung zählt deshalb keine eigenen Motoren, nur ihre Zeile) und die
    Rotation mit der Taille-Gier am Becken.
  */
  push(
    'Rumpf',
    'Wirbelsäule · Beugen und Strecken',
    1,
    staticMoment(trunkMass, 0.15, 1.4, load, 1, lever),
    'statisch geschätzt',
    0,
    `statisch geschätzt${carried}`,
    setAxisByKey('torso-pitch'),
  )
  push(
    'Rumpf',
    'Wirbelsäule · Seitneigung',
    1,
    staticMoment(trunkMass, 0.15, 1.0, load, 1, 0.4 * lever),
    'statisch geschätzt',
    0,
    `statisch geschätzt${carried}`,
    setAxisByKey('torso-roll'),
  )
  push(
    'Rumpf',
    'Wirbelsäule · Rotation',
    1,
    staticMoment(trunkMass, 0.15, 0.35, load, 1, 0.33 * lever),
    'statisch geschätzt',
    0,
    'kein Lastmoment aus der Schwerkraft',
    setAxisByKey('pelvis-yaw'),
  )
  push(
    'Kopf',
    'Hals · zwei Achsen',
    2,
    staticMoment(headMass, 0.09, 2.0, load),
    'statisch geschätzt',
  )

  /* Das Zehengrundgelenk ist passiv — Feder und Anschlag statt Antrieb. Es
     steht trotzdem in der Liste, weil seine Abwesenheit eine Entscheidung ist. */
  push('Beine', 'Zehengrundgelenk', 2, 0, 'passiv', 0, 'passiv, Feder und Anschlag statt Antrieb')

  return out
}

/* -------------------------------------------------------------------------
   Die Positionen ohne Antrieb
   ------------------------------------------------------------------------- */

export interface BomPart {
  key: string
  group: string
  label: string
  count: number
  estimate: number
  mass: number
  overridden: boolean
  /** Woher das Kilogramm kommt — dasselbe Vokabular wie bei den Achsen. */
  massSource: MassSource
  /** Der Nachweis aus dem PLM — `null`, solange keiner vorliegt. */
  evidence: BomEvidence | null
  note: string
  /** Kosten je Stück, EUR. Siehe `COST_RATES` — es sind Annahmen. */
  unitCost: number
  /** Kosten der Position, EUR. */
  cost: number
}

/* -------------------------------------------------------------------------
   Kosten
   ------------------------------------------------------------------------- */

/**
 * Stückkosten je Position, EUR.
 *
 * DIESE ZAHLEN SIND ANNAHMEN UND KEINE PREISE. Es gibt zu diesem Entwurf keine
 * Preisliste, keine Angebote und keinen Einkauf — die Vorlage führt in ihrer
 * Übersicht „Bauteile mit Massen und Kosten", liefert aber keine Kostendaten.
 *
 * Warum sie trotzdem hier stehen und nicht auf null: eine Kostenspalte voller
 * Nullen beantwortet keine Frage, und eine Stückliste ohne Kostenstruktur zeigt
 * nicht, WO das Geld sitzt. Die Grössenordnungen unten reichen dafür — sie
 * ordnen die Positionen zueinander, und genau das ist die Auskunft, die eine
 * frühe Auslegung braucht: dass die Antriebe das Doppelte der Elektronik
 * kosten, nicht dass sie 4200 Euro kosten.
 *
 * WER ECHTE ZAHLEN HAT, ersetzt diese Tabelle und ändert sonst nichts. Die
 * Rechnung ist Stückzahl mal Satz; es gibt keine zweite Stelle.
 *
 * Die Oberfläche schreibt an jede Summe, dass sie auf Annahmen beruht. Eine
 * Kostenzahl, die wie ein Angebot aussieht, ist der teuerste Irrtum, den eine
 * solche Liste anrichten kann.
 */
export const COST_RATES: Record<string, number> = {
  'f:compute': 3800,
  'f:power': 260,
  'f:bms': 900,
  'f:wiring': 1400,
  'f:imu': 1200,
  'f:encoder': 180,
  'f:camera': 320,
  'f:lidar': 1600,
  'f:feet': 550,
  'f:hands': 4500,
}

/** Kostensätze der massenabhängigen Positionen. */
export const COST_PER_KG = {
  /** Antriebsmodul: Motor, Getriebe, Geber, Gehäuse. */
  drive: 900,
  /** Struktur: Frästeile, Verbindungselemente, Fertigung. */
  structure: 420,
} as const

/** Kosten der Speicher, bezogen auf ihren Inhalt. */
export const COST_PER_WH = 5.5
export const COST_PER_FARAD = 45

/**
 * Erfahrungswerte für Elektronik, Sensorik und Anbauteile.
 *
 * Sie sind GESCHÄTZT und als solches gekennzeichnet — keine Katalogdaten. Zwei
 * Positionen hängen an der Achszahl, weil sie es wirklich tun: Leistungsstufen
 * und Gelenkgeber gibt es je Achse, und die Verkabelung wächst mit.
 */
function fixedParts(
  axisCount: number,
): Omit<
  BomPart,
  'mass' | 'overridden' | 'estimate' | 'unitCost' | 'cost' | 'massSource' | 'evidence'
>[] {
  return [
    {
      key: 'f:compute',
      group: 'Elektronik',
      label: 'Rechenplattform mit Kühlung',
      count: 1,
      note: 'Erfahrungswert',
    },
    {
      key: 'f:power',
      group: 'Elektronik',
      label: 'Leistungselektronik je Achse',
      count: axisCount,
      note: 'Erfahrungswert',
    },
    {
      key: 'f:bms',
      group: 'Elektronik',
      label: 'Energieverteilung und Batteriemanagement',
      count: 1,
      note: 'Erfahrungswert',
    },
    {
      key: 'f:wiring',
      group: 'Elektronik',
      label: 'Verkabelung und Steckverbinder',
      count: 1,
      note: 'wächst mit der Achszahl',
    },
    { key: 'f:imu', group: 'Sensorik', label: 'Inertialsensor', count: 1, note: 'Erfahrungswert' },
    {
      key: 'f:encoder',
      group: 'Sensorik',
      label: 'Gelenkgeber, zwei je Achse',
      count: axisCount,
      note: 'Erfahrungswert',
    },
    { key: 'f:camera', group: 'Sensorik', label: 'Kameras', count: 2, note: 'Erfahrungswert' },
    {
      key: 'f:lidar',
      group: 'Sensorik',
      label: 'Umgebungsscanner',
      count: 1,
      note: 'Erfahrungswert',
    },
    {
      key: 'f:feet',
      group: 'Sonstiges',
      label: 'Füsse mit Sohle',
      count: 2,
      note: 'Erfahrungswert',
    },
    {
      key: 'f:hands',
      group: 'Sonstiges',
      label: 'Hände oder Greifer',
      count: 2,
      note: 'Erfahrungswert',
    },
  ]
}

const FIXED_MASS: Record<string, number | ((axes: number) => number)> = {
  'f:compute': 0.65,
  'f:power': 0.09,
  'f:bms': 0.5,
  'f:wiring': (axes) => 0.9 + 0.025 * axes,
  'f:imu': 0.05,
  'f:encoder': 0.04,
  'f:camera': 0.15,
  'f:lidar': 0.3,
  'f:feet': 0.45,
  'f:hands': 0.4,
}

/* -------------------------------------------------------------------------
   Das Ergebnis
   ------------------------------------------------------------------------- */

export interface BomResult {
  axes: BomAxis[]
  parts: BomPart[]
  /** Zahl der angetriebenen Achsen. */
  drivenAxes: number
  /** Achsen, die mehr Drehzahl verlangen als das Modul liefert. */
  tooFastAxes: number
  /** Masse aller Antriebe, kg. */
  actuatorMass: number

  /** Mittlere Antriebsleistung über den Zyklus, W. */
  averagePower: number
  /** Verlangte Akkuenergie, Wh. */
  batteryEnergy: number
  batteryEstimate: number
  batteryMass: number
  batteryOverridden: boolean

  /** Spitzenleistung am Zwischenkreis, W. */
  peakPower: number
  /** Energie über dem Mittelwert, die gepuffert werden muss, J. */
  burstEnergy: number
  /** Dauer, über die das anfällt, s. */
  burstTime: number
  /** Untere Zwischenkreisspannung, V. */
  minVoltage: number
  /** Verlangte Kapazität, F. */
  capacitance: number
  capacitorEstimate: number
  capacitorMass: number
  capacitorOverridden: boolean
  /** Spitzenstrom am Zwischenkreis, A. */
  peakCurrent: number
  /** Rückspeisespitze, W. */
  brakingPeak: number

  /** Massen je Gruppe, kg. */
  electronicsMass: number
  sensorMass: number
  miscMass: number
  structureMass: number
  /** Summe ohne Struktur, kg. */
  coreMass: number
  /** Gesamtmasse aus der Stückliste, kg. */
  total: number

  /** Anteil, der mit der Masse wächst. */
  scalingShare: number
  /** Fester Anteil, kg. */
  fixedMass: number
  /** Fixpunkt der Massenschleife, kg. Unendlich, wenn der Entwurf divergiert. */
  fixedPoint: number
  /** Verstärkung der Schleife. */
  amplification: number

  /** Abweichung gegen die eingestellte Körpermasse, kg. */
  deviation: number
  /** Wie viele Positionen überschrieben sind. */
  overrides: number
  /**
   * Wie belastbar die Massen sind — je Herkunft die Zahl der Positionen und
   * die Masse, die sie beisteuern.
   *
   * DIE EIGENTLICHE KENNZAHL DER SEITE, und sie fehlte. Eine Gesamtmasse sagt
   * WIE VIEL. Sie sagt nicht, wie viel davon jemand gemessen und wie viel
   * jemand geschaetzt hat — und das ist die Frage, an der eine Freigabe
   * haengt.
   *
   * Solange `plm.parts` null ist, ist die Antwort unbequem und richtig: nichts
   * an dieser Maschine ist gewogen.
   */
  provenance: Record<MassSource, { parts: number; mass: number }>

  /**
   * Kosten je Gruppe und in Summe, EUR — ALLES ANNAHMEN.
   *
   * Sie stehen als eigener Block und nicht neben den Massen, damit niemand sie
   * für gleichwertig hält: die Massen folgen aus dem Modell und den
   * Antriebsdaten, die Kosten aus einer Tabelle von Grössenordnungen. Siehe
   * `COST_RATES`.
   */
  cost: {
    actuators: number
    battery: number
    capacitor: number
    electronics: number
    sensors: number
    misc: number
    structure: number
    total: number
  }
}

/**
 * Rechnet die Stückliste.
 *
 * `netPower` ist der Leistungsverlauf am Zwischenkreis über den Zyklus — die
 * Summe über alle Achsen, nach Rückspeisung. Ohne ihn bleiben Akku und
 * Kondensator leer; sie sind die beiden Positionen, die man nicht schätzen
 * kann, ohne den Verlauf zu kennen.
 */
export function computeBom(
  model: HumanoidModel,
  demands: AxisDemand[],
  state: SimState,
  params: BomParams,
  cycleTime: number,
  netPower: Float32Array | number[] | null,
  overrides: Record<string, number> = {},
  /*
    Die Nachweise aus dem PLM, nach Stuecklistenschluessel.

    HEUTE IMMER LEER. Das PLM ist angebunden und liefert Segmentdaten, aber
    keine Zuordnung von Teilen zu Stuecklistenpositionen — dafuer gibt es
    `PlmPart.bomKey`, und das fuellt die Gegenstelle noch nicht. Der Weg ist
    trotzdem gebaut: sobald etwas kommt, tragen die Zeilen es, ohne dass hier
    noch etwas zu aendern waere.
  */
  evidenceMap: BomEvidenceMap = {},
): BomResult {
  const axes = bomAxes(model, demands, state, params, overrides, evidenceMap)

  let drivenAxes = 0
  let tooFastAxes = 0
  let actuatorMass = 0
  for (const axis of axes) {
    if (axis.moment > 0) drivenAxes += axis.count
    if (axis.tooFast) tooFastAxes += axis.count
    actuatorMass += axis.mass * axis.count
  }

  /*
    ENERGIE AUS DER ANTRIEBSARBEIT. Die mittlere Leistung ist das Mittel des
    Zwischenkreisverlaufs — nicht die Summe der Spitzen. Eine Achse, die bremst,
    während eine andere treibt, entlastet den Akku, und genau das steht im
    Nettoverlauf.
  */
  let averagePower = 0
  let peakPower = 0
  let burstEnergy = 0
  let burstTime = 0
  let brakingPeak = 0

  if (netPower && netPower.length > 1 && cycleTime > 0) {
    const n = netPower.length - 1
    const dt = cycleTime / n
    for (let i = 0; i < n; i++) {
      averagePower += netPower[i]
      if (netPower[i] > peakPower) peakPower = netPower[i]
      if (-netPower[i] > brakingPeak) brakingPeak = -netPower[i]
    }
    averagePower /= n
    /* Puffern muss nur, was ÜBER dem Mittelwert liegt: die Fläche zwischen
       Verlauf und Mittelwert. Der Rest kommt aus dem Akku. */
    for (let i = 0; i < n; i++) {
      const excess = netPower[i] - averagePower
      if (excess > 0) {
        burstEnergy += excess * dt
        burstTime += dt
      }
    }
  }

  const batteryEnergy = Math.max(0, averagePower) * params.runtime
  const batteryEstimate = batteryEnergy / params.batteryDensity
  const batteryOverridden = overrides.bat !== undefined
  const batteryMass = batteryOverridden ? overrides.bat : batteryEstimate

  /*
    KONDENSATOR. C = 2E/(U² − Umin²): ein Kondensator gibt nur die Energie
    zwischen voller Spannung und der niedrigsten noch zulässigen ab. Wer mit
    ½CU² rechnet, bekommt eine um den Faktor zwei bis drei zu kleine Kapazität.
  */
  const minVoltage = params.busVoltage * (1 - params.voltageDrop / 100)
  const capacitance =
    burstEnergy > 0
      ? (2 * burstEnergy) / (params.busVoltage * params.busVoltage - minVoltage * minVoltage)
      : 0
  const storedEnergy = 0.5 * capacitance * params.busVoltage * params.busVoltage
  /* 5 Wh/kg Zellenergiedichte, Faktor 2,2 für Gehäuse und Symmetrierung. */
  const capacitorEstimate = (storedEnergy / 3600 / 5) * 2.2
  const capacitorOverridden = overrides.cap !== undefined
  const capacitorMass = capacitorOverridden ? overrides.cap : capacitorEstimate

  const peakCurrent = params.busVoltage > 0 ? peakPower / params.busVoltage : 0

  const parts: BomPart[] = fixedParts(drivenAxes).map((spec) => {
    const raw = FIXED_MASS[spec.key]
    const estimate = typeof raw === 'function' ? raw(drivenAxes) : raw
    const overridden = overrides[spec.key] !== undefined
    const unitCost = COST_RATES[spec.key] ?? 0
    /* Dieselbe Rangfolge wie bei den Achsen, nur ohne die Stufe „gesetzt":
       fuer Elektronik und Sensorik gibt es keine Auswahlmatrix. */
    const evidence = evidenceMap[spec.key] ?? null
    return {
      ...spec,
      estimate,
      mass: overridden ? overrides[spec.key] : (evidence?.mass ?? estimate),
      overridden,
      massSource: overridden ? 'ueberschrieben' : evidence ? 'plm' : 'geschaetzt',
      evidence,
      note: overridden ? 'gesetzt' : spec.note,
      unitCost,
      cost: spec.count * unitCost,
    }
  })

  const sumGroup = (group: string) =>
    parts.filter((p) => p.group === group).reduce((a, p) => a + p.count * p.mass, 0)

  const electronicsMass = sumGroup('Elektronik')
  const sensorMass = sumGroup('Sensorik')
  const miscMass = sumGroup('Sonstiges')

  const coreMass =
    actuatorMass + batteryMass + capacitorMass + electronicsMass + sensorMass + miscMass
  const total = coreMass / (1 - params.structureShare)
  const structureMass = total - coreMass

  /*
    DIE SCHLEIFE. Moment und Antriebsarbeit wachsen mit der Masse, Elektronik
    und Sensorik nicht. Aus m′ = (a·m + c)/(1 − k) folgt der Fixpunkt
    m* = c/((1 − k) − a). Wird der Nenner klein, kann die Maschine ihre eigenen
    Antriebe nicht mehr tragen.
  */
  const scalingShare =
    state.body > 0 ? (actuatorMass + batteryMass + capacitorMass) / state.body : 0
  const fixedMass = electronicsMass + sensorMass + miscMass
  const denominator = 1 - params.structureShare - scalingShare
  const diverges = denominator <= 0.02
  const fixedPoint = diverges ? Infinity : fixedMass / denominator
  const amplification = diverges ? Infinity : (1 - params.structureShare) / denominator

  /*
    KOSTEN — ALLES ANNAHMEN, siehe `COST_RATES`. Die massenabhängigen Positionen
    werden über einen Satz je Kilogramm gerechnet, die Speicher über ihren
    Inhalt. Das ist grob, und grob genügt: die Frage, die eine frühe Auslegung
    an eine Stückliste stellt, ist „wo sitzt das Geld", nicht „was kostet es".
  */
  const costGroup = (group: string) =>
    parts.filter((p) => p.group === group).reduce((a, p) => a + p.cost, 0)

  const cost = {
    actuators: actuatorMass * COST_PER_KG.drive,
    battery: batteryEnergy * COST_PER_WH,
    capacitor: capacitance * COST_PER_FARAD,
    electronics: costGroup('Elektronik'),
    sensors: costGroup('Sensorik'),
    misc: costGroup('Sonstiges'),
    structure: structureMass * COST_PER_KG.structure,
    total: 0,
  }
  cost.total =
    cost.actuators +
    cost.battery +
    cost.capacitor +
    cost.electronics +
    cost.sensors +
    cost.misc +
    cost.structure

  return {
    axes,
    parts,
    drivenAxes,
    tooFastAxes,
    actuatorMass,
    averagePower,
    batteryEnergy,
    batteryEstimate,
    batteryMass,
    batteryOverridden,
    peakPower,
    burstEnergy,
    burstTime,
    minVoltage,
    capacitance,
    capacitorEstimate,
    capacitorMass,
    capacitorOverridden,
    peakCurrent,
    brakingPeak,
    electronicsMass,
    sensorMass,
    miscMass,
    structureMass,
    coreMass,
    total,
    scalingShare,
    fixedMass,
    fixedPoint,
    amplification,
    deviation: total - state.body,
    overrides: Object.keys(overrides).length,
    provenance: countProvenance(axes, parts),
    cost,
  }
}

/**
 * Die ROHE mechanische Summe über alle Achsen, W je Abtastpunkt.
 *
 * Nicht die Summe der Spitzen: eine Achse, die bremst, während eine andere
 * treibt, entlastet den Akku, und genau darin liegt der Unterschied zwischen
 * einer ehrlichen und einer ängstlichen Auslegung.
 *
 * DIES IST NICHT DIE AKKULEISTUNG. Die Reihe wird über den Gehzyklus im Mittel
 * NEGATIV — die inverse Dynamik ist verlustfrei und gewinnt beim Bremsen mehr
 * zurück, als sie beim Treiben ausgibt. Was der Akku wirklich liefern muss,
 * rechnet `computePower`; diese Funktion steht daneben als die untere Schranke,
 * gegen die man das Ergebnis hält.
 */
export function mechanicalPower(demands: AxisDemand[]): Float32Array | null {
  const driven = demands.filter((d) => !d.structural && d.powerTrack.length > 0)
  if (driven.length === 0) return null

  const n = driven[0].powerTrack.length
  const out = new Float32Array(n)
  for (const demand of driven) {
    const track = demand.powerTrack
    if (track.length !== n) continue
    for (let i = 0; i < n; i++) out[i] += track[i]
  }
  return out
}

/**
 * Zaehlt Positionen und Masse je Herkunft.
 *
 * Achsen UND feste Positionen, weil beide in dieselbe Gesamtmasse laufen. Die
 * Struktur bleibt draussen: sie ist kein Posten, sondern ein Anteil, und ihre
 * Herkunft ist die Annahme `structureShare` — sie faende sich sonst als
 * „geschaetzt" wieder und wuerde die Zahl beschoenigen, indem sie sie
 * verwaessert.
 */
function countProvenance(
  axes: BomAxis[],
  parts: BomPart[],
): Record<MassSource, { parts: number; mass: number }> {
  const out: Record<MassSource, { parts: number; mass: number }> = {
    plm: { parts: 0, mass: 0 },
    ueberschrieben: { parts: 0, mass: 0 },
    gesetzt: { parts: 0, mass: 0 },
    geschaetzt: { parts: 0, mass: 0 },
  }
  for (const row of [...axes, ...parts]) {
    if (row.mass <= 0) continue
    const eintrag = out[row.massSource]
    eintrag.parts += row.count
    eintrag.mass += row.mass * row.count
  }
  return out
}

